'use client';

import { type ButtonHTMLAttributes, forwardRef, type HTMLAttributes, type ReactNode, useEffect, useState } from 'react';
import { AudioDom } from '@/app/components/shared/AudioDom';
import type { AudioOption } from '@/app/types';
import { cn } from '@/app/utils';
import { getBlurBackgroundImg } from '@/app/utils/client';

interface AudioPreviewCardProps {
  audio: AudioOption;
  blurHash?: string;
  title?: string;
  description?: string;
  action?: ReactNode;
  interactive?: boolean;
  className?: string;
}

export const AudioPreviewCard = forwardRef<
  HTMLButtonElement | HTMLDivElement,
  AudioPreviewCardProps & HTMLAttributes<HTMLDivElement> & ButtonHTMLAttributes<HTMLButtonElement>
>(({ audio, blurHash, title, description, action, interactive = false, className, style, ...props }, ref) => {
  const [blurBackgroundImage, setBlurBackgroundImage] = useState('');

  useEffect(() => {
    setBlurBackgroundImage(getBlurBackgroundImg(blurHash, 0.1));
  }, [blurHash]);

  const content = (
    <div className="flex w-full items-center gap-3">
      <div className="relative shrink-0">
        <div className="absolute inset-0 rounded-[10px] bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.14),transparent_60%)]" />
        <AudioDom
          audio={audio}
          className="size-16 rounded-[10px] border-system-white08"
          playIconClassName="size-5"
          animation={false}
          imageOverlay
        />
      </div>
      <div className="min-w-0 flex-1 py-1">
        <div className="truncate font-medium text-base text-system-white leading-5">{title ?? audio.name}</div>
        <div className="truncate text-sm text-system-white/40 leading-5">{description ?? audio.description}</div>
      </div>
      {action ? <div className="flex shrink-0 items-center self-center text-system-white/64">{action}</div> : null}
    </div>
  );

  const cardClassName = cn(
    'h-full min-h-20 w-full rounded-16 border border-system-white08 bg-cover bg-transparent bg-no-repeat px-[14px] py-2 text-left',
    interactive && 'transition-colors hover:bg-white/10',
    className,
  );
  const cardStyle = {
    backgroundImage: blurBackgroundImage ? `url(${blurBackgroundImage})` : '',
    ...style,
  };

  if (interactive) {
    const buttonProps = props as ButtonHTMLAttributes<HTMLButtonElement>;

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={buttonProps.type ?? 'button'}
        className={cardClassName}
        style={cardStyle}
        {...buttonProps}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={cardClassName}
      style={cardStyle}
      {...(props as HTMLAttributes<HTMLDivElement>)}
    >
      {content}
    </div>
  );
});

AudioPreviewCard.displayName = 'AudioPreviewCard';
